import { useEffect, useRef } from "react";

const LOGO_URL = "https://public.readdy.ai/ai/img_res/40beb105-aa73-4e53-a2f4-5cf10142dfbd.png";

const stats = [
  { value: "38%", prefix: "+", label: "Increase in lead capture" },
  { value: "2.5x", prefix: "", label: "Faster decision cycles" },
  { value: "22%", prefix: "+", label: "Lift in conversion rate" },
];

export default function HeroSection() {
  const tagRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLHeadingElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = [tagRef.current, headRef.current, subRef.current, ctaRef.current, statsRef.current];
    els.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(30px)";
      setTimeout(() => {
        if (!el) return;
        el.style.transition = "opacity 0.9s cubic-bezier(0.22,1,0.36,1), transform 0.9s cubic-bezier(0.22,1,0.36,1)";
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      }, 100 + i * 130);
    });
  }, []);

  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="hero" className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-bfx-graphite">
      {/* Full-bleed background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/519fa8bac21a3043cf0435589d717a57.jpg"
          alt=""
          className="w-full h-full object-cover object-center opacity-40"
        />
      </div>

      {/* Layered gradients for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-bfx-graphite/70 via-bfx-graphite/30 to-bfx-graphite/90 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-bfx-primary/30 via-transparent to-transparent pointer-events-none" />

      {/* Subtle grid */}
      <div className="absolute inset-0 grid-bg-dark opacity-40 pointer-events-none" />

      {/* Watermark logo */}
      <div className="absolute right-[-80px] top-1/2 -translate-y-1/2 w-[600px] h-[600px] flex items-center justify-center pointer-events-none select-none opacity-[0.03]">
        <img src={LOGO_URL} alt="" className="w-full h-full object-contain" />
      </div>

      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-bfx-accent/50 to-transparent" />

      {/* Content — centered like Apple */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 md:px-10 text-center pt-24 pb-20">
        {/* Eyebrow tag */}
        <div ref={tagRef} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/15 bg-white/8 mb-8 backdrop-blur-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-bfx-accent" />
          <span className="text-[11px] font-semibold text-white/70 tracking-[0.12em] uppercase">
            Financial Intelligence Infrastructure
          </span>
        </div>

        {/* Headline — Apple-scale typography */}
        <h1
          ref={headRef}
          className="text-5xl sm:text-6xl md:text-7xl lg:text-[82px] font-semibold leading-[1.05] tracking-tight text-white mb-6"
        >
          Precision Intelligence<br />
          <span
            style={{
              background: "linear-gradient(135deg, #6ab0ff 0%, #3A8DFF 40%, #2C4E73 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            for Financial Growth.
          </span>
        </h1>

        {/* Sub */}
        <p
          ref={subRef}
          className="text-base md:text-lg text-white/55 leading-relaxed max-w-2xl mx-auto mb-10"
        >
          Intelligent systems that capture opportunities, streamline decisions,
          and accelerate revenue across financial and real estate operations.
        </p>

        {/* CTAs — pill style */}
        <div ref={ctaRef} className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => scrollTo("#services")}
            className="whitespace-nowrap px-8 py-3.5 rounded-full bg-bfx-accent text-white text-sm font-semibold hover:bg-[#2878e8] transition-all duration-200 cursor-pointer"
          >
            Build Your Intelligence System
          </button>
          <button
            onClick={() => scrollTo("#cta")}
            className="whitespace-nowrap px-8 py-3.5 rounded-full border border-white/20 text-white text-sm font-medium hover:bg-white/10 transition-all duration-200 cursor-pointer backdrop-blur-sm"
          >
            Schedule Strategy Call
            <i className="ri-arrow-right-line ml-2 text-xs" />
          </button>
        </div>

        {/* Stats — Apple-like large numbers */}
        <div ref={statsRef} className="mt-20 pt-12 border-t border-white/10 grid grid-cols-3 gap-8 max-w-2xl mx-auto">
          {stats.map((s) => (
            <div key={s.label}>
              <p className="text-3xl md:text-4xl font-semibold text-white tabular-nums">
                {s.prefix}<span>{s.value}</span>
              </p>
              <p className="text-xs text-white/40 mt-1.5 leading-snug">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom gradient transition to white */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </section>
  );
}